import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "pt-3" }
const _hoisted_2 = { class: "open-map-container col-md-6 col-10 bg-primary-white d-flex flex-column align-items-center gap-3" }
const _hoisted_3 = { class: "title-3 text-grey-900 mt-1 col-12" }
const _hoisted_4 = { class: "buttons-small text-primary-blue mt-1" }
const _hoisted_5 = {
  style: {"height":"24px","width":"24px"},
  class: "icon tf-icon-16 text-blue-900"
}

import { ref, onMounted, watch} from "vue";
// components
import MapComponent from "@/vue/map/components/map-v2.vue";
// composables
import useMapContainerVisibility from "@/vue/composables/useMapContainerVisibility";

// Images
import mapPlaceHolder from "@/images/mapPlaceHolder.png"

/**
 * Reactive reference tracking whether the map component is currently open/visible.
 * When true, the MapComponent is conditionally rendered.
 * 
 * @type {Ref<boolean>}
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'App-v2',
  setup(__props) {

/**
 * Map Container Component (App-v2.vue)
 * 
 * This component displays a placeholder map with a button to open the full map view.
 * When the button is clicked, it conditionally renders the MapComponent which handles
 * fullscreen display (desktop) or CSS overlay (iOS Safari).
 * 
 * @component
 */

// Vue
const isMapOpen = ref(false);

/**
 * Composable for toggling map container visibility.
 */
const { toggleMapContainerVisibility } = useMapContainerVisibility();

/**
 * Watcher for isMapOpen that toggles the d-none class on vue-map-container.
 */
watch(isMapOpen, (newValue) => {
	toggleMapContainerVisibility(newValue);
});

/**
 * Handles the click event on the "View on map" button.
 * 
 * This function toggles the map component visibility:
 * - If map is closed: Opens it and dispatches 'openMapClick' event
 *   (MapComponent will auto-trigger fullscreen/overlay on mount)
 * - If map is open: Dispatches 'openMapClick' event to close overlay/fullscreen,
 *   then closes the component (cleanup happens in MapComponent's beforeUnmount)
 * 
 * @function handleOpenMapClick
 * @returns {void}
 */
const handleOpenMapClick = (): void => {
	if (isMapOpen.value) {
		// Map is already open, close it
		// Dispatch event to trigger toggle (will close overlay/fullscreen)
		window.dispatchEvent(new Event("openMapClick"))
		// Close the component (beforeUnmount will clean up overlay)
		isMapOpen.value = false
	} else {
		// Map is closed, open it
		isMapOpen.value = true
		// The MapComponent will auto-trigger fullscreen/overlay when it mounts
		// We still dispatch the event for cases where component is already mounted
		window.dispatchEvent(new Event("openMapClick"))
	}
}

const content = window.content
const lihe = window.lihe

/**
 * Lifecycle hook: Sets up event listeners for fullscreen changes.
 * 
 * Listens to fullscreen API events from all browser vendors to detect when
 * the user exits fullscreen mode (e.g., pressing ESC). When fullscreen is
 * exited, the map component is automatically closed.
 * 
 * Supported browser prefixes:
 * - Standard: fullscreenchange
 * - WebKit (Safari, Chrome): webkitfullscreenchange
 * - Mozilla (Firefox): mozfullscreenchange
 * - Microsoft (IE/Edge): MSFullscreenChange
 * 
 * @function onMounted
 * @returns {void}
 */
onMounted(() => {
	// Initialize the visibility state based on initial isMapOpen value
	toggleMapContainerVisibility(isMapOpen.value);
	
	// Handle fullscreen changes (desktop browsers)
	document.addEventListener("fullscreenchange", () => {
		const doc = document as any;
		if (!document.fullscreenElement && 
			!doc['webkitFullscreenElement'] && 
			!doc['mozFullScreenElement'] && 
			!doc['msFullscreenElement']) {
			isMapOpen.value = false;
		}
	});
	
	// Also listen for webkit fullscreen events (Safari desktop)
	document.addEventListener("webkitfullscreenchange", () => {
		const doc = document as any;
		if (!doc['webkitFullscreenElement']) {
			isMapOpen.value = false;
		}
	});
	
	document.addEventListener("mozfullscreenchange", () => {
		const doc = document as any;
		if (!doc['mozFullScreenElement']) {
			isMapOpen.value = false;
		}
	});
	
	document.addEventListener("MSFullscreenChange", () => {
		const doc = document as any;
		if (!doc['msFullscreenElement']) {
			isMapOpen.value = false;
		}
	});
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "map-container d-flex justify-content-center align-items-center",
      style: _normalizeStyle([{
			'background-image': `url(${_unref(mapPlaceHolder)})`
		}, {"width":"100%","height":"400px","border-radius":"var(--border-radius-card-secondary)"}])
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(content)['map_location_accommodation']) + " " + _toDisplayString(_unref(lihe)?.name || ""), 1),
        _createElementVNode("button", {
          onClick: handleOpenMapClick,
          type: "button",
          class: "button-secondary bg-none border-primary-blue text-primary-blue d-flex justify-content-evenly open-map-button col-md-4 col-12"
        }, [
          _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(content)['map_seeMap']), 1),
          _createElementVNode("div", null, [
            (_openBlock(), _createElementBlock("svg", _hoisted_5, _cache[0] || (_cache[0] = [
              _createElementVNode("use", { "xlink:href": "#___icon___marker" }, null, -1)
            ])))
          ])
        ])
      ])
    ], 4),
    (isMapOpen.value)
      ? (_openBlock(), _createBlock(MapComponent, { key: 0 }))
      : _createCommentVNode("", true)
  ]))
}
}

})